import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { expensesAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const EXPENSE_CATEGORIES = [
  { value: 'REPAIR', label: 'Repair' },
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous' },
];

const Expenses = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ category: '', start_date: '', end_date: '' });
  const [formData, setFormData] = useState({
    category: '', amount: '', description: '', expense_date: '', notes: '',
  });

  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const response = await expensesAPI.list(selectedStore.id, params);
      setExpenses(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, filters]);

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const response = await expensesAPI.getSummary(selectedStore.id, params);
      setSummary(response.data);
    } catch (err) {
      // Summary is optional
    }
  }, [selectedStore, filters]);

  useEffect(() => {
    if (selectedStore) {
      loadExpenses();
      loadSummary();
    }
  }, [selectedStore, loadExpenses, loadSummary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, store_id: selectedStore.id, amount: parseFloat(formData.amount) };
      if (editingId) {
        await expensesAPI.update(editingId, data);
        showToast('Expense updated successfully', 'success');
      } else {
        await expensesAPI.create(data);
        showToast('Expense recorded successfully', 'success');
      }
      loadExpenses();
      loadSummary();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      category: expense.category || '',
      amount: expense.amount || '',
      description: expense.description || '',
      expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
      notes: expense.notes || '',
    });
    setEditingId(expense.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await expensesAPI.delete(id);
      showToast('Expense deleted', 'success');
      loadExpenses();
      loadSummary();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete expense'));
    }
  };

  const resetForm = () => {
    setFormData({ category: '', amount: '', description: '', expense_date: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading expenses...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Expenses</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ Add Expense'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="filter-bar">
        <div className="form-group">
          <label className="form-label">Category</label>
          <select className="form-select" value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
            <option value="">All Categories</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input type="date" className="form-input" value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input type="date" className="form-input" value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
      </div>

      {summary && (
        <div className="kpi-grid">
          <div className="kpi-card primary">
            <div className="kpi-label">Total Expenses</div>
            <div className="kpi-value">${parseFloat(summary.total || 0).toFixed(2)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">This Month</div>
            <div className="kpi-value">${parseFloat(summary.this_month || 0).toFixed(2)}</div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-label">Count</div>
            <div className="kpi-value">{summary.count || expenses.length}</div>
          </div>
          {summary.average && (
            <div className="kpi-card">
              <div className="kpi-label">Average</div>
              <div className="kpi-value">${parseFloat(summary.average).toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Expense' : 'Add New Expense'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })} required>
                    <option value="">Select Category</option>
                    {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input type="number" className="form-input" value={formData.amount} step="0.01" min="0"
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <input type="text" className="form-input" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-input" value={formData.expense_date}
                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes} rows={3}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">{editingId ? 'Update Expense' : 'Create Expense'}</button>
                {editingId && <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {expenses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🧾</div>
            <h3 className="empty-state-title">No expenses yet</h3>
            <p>Add your first expense to get started</p>
          </div>
        ) : (
          expenses.map(expense => (
            <div className="mobile-card" key={expense.id}>
              <div className="mobile-card-header">
                <div>
                  <div className="mobile-card-title">{expense.description}</div>
                  <span className="badge badge-info">{expense.category}</span>
                </div>
                <span className="font-bold">${parseFloat(expense.amount).toFixed(2)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Date</span>
                <span>{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString() : '-'}</span>
              </div>
              <div className="mobile-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(expense)}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(expense.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(expense => (
                <tr key={expense.id}>
                  <td>{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString() : '-'}</td>
                  <td><span className="badge badge-info">{expense.category}</span></td>
                  <td>{expense.description}</td>
                  <td className="font-bold">${parseFloat(expense.amount).toFixed(2)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(expense)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(expense.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr><td colSpan="5" className="text-center text-muted" style={{ padding: '3rem' }}>No expenses found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Expenses;
